import type { Page } from '../../../../common/page/page.model';
import type { Comment } from '../../../model/comment.interface';

/**
 * A notification email's "View Comment" link: the ids ride the query string ahead of the
 * hash route (`?comment=…&parent=…#/posts/…`), like `?showEdit=true` on the account page,
 * so the router never sees them. `parent` is set for a reply.
 */
export interface CommentDeepLink {
  commentId: string;
  parentCommentId: string | null;
}

/** How many extra pages (of comments, then of replies) to fetch looking for the target. */
export const DEEP_LINK_MAX_PAGES = 5;

export type DeepLinkStep =
  | { kind: 'found' }
  | { kind: 'load-page'; parentCommentId: string | null; page: number }
  | { kind: 'give-up' };

/** The older `?comment=true` link means "focus the comment box" (the form still honours it), not an id. */
const LEGACY_FOCUS_FLAG = 'true';

export const readCommentDeepLink = (search: string): CommentDeepLink | null => {
  const params = new URLSearchParams(search);
  const commentId = params.get('comment');
  if (!commentId || commentId === LEGACY_FOCUS_FLAG) return null;
  return { commentId, parentCommentId: params.get('parent') || null };
};

/**
 * Drops the deep-link params from the address bar once the link has been acted on. The hash
 * router only rewrites the hash, so the query string rides along on every route change in the
 * session — without this, each later visit to the post (from the feed, the top-comment strip,
 * the back button) would read the ids again and replay the scroll and highlight.
 */
export const clearCommentDeepLink = (): void => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('comment') && !params.has('parent')) return;
  params.delete('comment');
  params.delete('parent');
  const search = params.toString();
  const { pathname, hash } = window.location;
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${search ? `?${search}` : ''}${hash}`
  );
};

export const findCommentById = (
  content: Comment[] | null | undefined,
  id: string
): Comment | null => {
  if (!content) return null;
  for (const comment of content) {
    if (comment.id === id) return comment;
    if (comment.children?.content) {
      const found = findCommentById(comment.children.content, id);
      if (found) return found;
    }
  }
  return null;
};

const nextPageOf = (page: Page<Comment> | null | undefined): number | null => {
  if (!page) return 0;
  if (page.last) return null;
  return page.number + 1;
};

/**
 * Decides what to do next to bring the linked comment into view. Comments load newest-first
 * in pages, and a reply's parent carries the first page of its replies; the store appends
 * further pages of either, so the search is "is it here yet? if not, fetch the next page" —
 * top-level pages until the comment (or its parent) shows up, then the parent's reply pages.
 */
export const nextDeepLinkStep = (
  comments: Page<Comment> | null | undefined,
  link: CommentDeepLink,
  pagesLoaded: number,
  maxPages = DEEP_LINK_MAX_PAGES
): DeepLinkStep => {
  if (!comments) return { kind: 'give-up' };
  if (findCommentById(comments.content, link.commentId)) return { kind: 'found' };
  if (pagesLoaded >= maxPages) return { kind: 'give-up' };

  const parent = link.parentCommentId
    ? findCommentById(comments.content, link.parentCommentId)
    : null;
  if (parent) {
    const page = nextPageOf(parent.children);
    return page === null
      ? { kind: 'give-up' }
      : { kind: 'load-page', parentCommentId: parent.id, page };
  }
  const page = nextPageOf(comments);
  return page === null ? { kind: 'give-up' } : { kind: 'load-page', parentCommentId: null, page };
};
